/* Audit tab — historical Telegram backfill + parser QA report.
   Extracted from Dashboard.jsx during the 2026-02-14d refactor.
   Owns its own DateField helper since it's not used by other tabs. */
import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { format, subDays, startOfMonth, startOfYear } from "date-fns";
import { channels, telegram } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge, MetricCard } from "@/components/atoms";
import { ToggleRow } from "@/components/dashboard/formfields";
import { CalendarBlank, ArrowsClockwise } from "@phosphor-icons/react";

const ACTION_COLORS = {
  entry: "#059669",
  close: "#DC2626",
  bulk_close: "#DC2626",
  partial_close: "#D97706",
  sl_update: "#4F46E5",
  sl_to_breakeven: "#0EA5E9",
  tp_update: "#0EA5E9",
  cancel: "#9CA0B8",
  noise: "#9CA0B8",
  unknown: "#D97706",
};

function DateField({ label, value, onChange, testId, minDate, maxDate }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-1.5">
      <Label className="overline">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            data-testid={testId}
            type="button"
            className="w-full h-10 flex items-center justify-between px-3 rounded-md bg-white border border-slate-200 hover:border-slate-300 focus:border-[#4F46E5] focus:outline-none transition-colors mono text-sm text-slate-900"
          >
            <span>{value ? format(value, "MMM d, yyyy") : "Pick a date"}</span>
            <CalendarBlank size={16} color="#71717A" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0 bg-white border-slate-200"
          align="start"
          data-testid={`${testId}-popover`}
        >
          <Calendar
            mode="single"
            selected={value}
            defaultMonth={value}
            onSelect={(d) => {
              if (!d) return;
              onChange(d);
              setOpen(false);
            }}
            disabled={(d) =>
              (minDate && d < new Date(minDate.setHours(0, 0, 0, 0))) ||
              (maxDate && d > new Date(maxDate.setHours(23, 59, 59, 999)))
            }
            initialFocus
            className="text-slate-900"
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function AuditSection() {
  const today = new Date();
  const [fromDate, setFromDate] = useState(subDays(today, 90));
  const [toDate, setToDate] = useState(today);
  const [dryRun, setDryRun] = useState(true);
  const [useAi, setUseAi] = useState(false);
  const [chatId, setChatId] = useState("");
  const [aiCap, setAiCap] = useState(200);
  const [report, setReport] = useState(null);
  const channelsQ = useQuery({
    queryKey: ["channels"], queryFn: async () => (await channels.list()).data,
  });

  const toIso = (d) => format(d, "yyyy-MM-dd");

  const run = useMutation({
    mutationFn: () => telegram.backfill(toIso(fromDate), toIso(toDate), dryRun, useAi, chatId || null, useAi ? aiCap : null),
    onSuccess: (r) => {
      setReport(r.data);
      toast.success(`Analyzed ${r.data.total_messages} messages → ${r.data.total_parsed_signals} signals`);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Backfill failed"),
  });

  const applyPreset = (from, to = new Date()) => {
    setFromDate(from);
    setToDate(to);
  };

  const presets = [
    { label: "7d", get: () => [subDays(new Date(), 7), new Date()] },
    { label: "30d", get: () => [subDays(new Date(), 30), new Date()] },
    { label: "90d", get: () => [subDays(new Date(), 90), new Date()] },
    { label: "MTD", get: () => [startOfMonth(new Date()), new Date()] },
    { label: "YTD", get: () => [startOfYear(new Date()), new Date()] },
  ];

  const totalSignals = report?.total_parsed_signals || 0;
  const actions = report?.action_counts || {};
  const sortedActions = Object.entries(actions).sort(([, a], [, b]) => b - a);
  const maxCount = sortedActions[0]?.[1] || 1;
  const recall = totalSignals ? (1 - (actions.unknown || 0) / totalSignals) * 100 : 0;

  return (
    <div className="space-y-4" data-testid="section-audit">
      <div className="card-surface p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="overline">Channel Analyzer</p>
            <h3 className="font-display text-xl mt-1">Historical Signal Analyzer</h3>
            <p className="text-xs text-slate-500 mt-1">
              Reach for this before adding a new channel, or if a channel's parsing looks
              off: pull its historical messages, run them through the same parser the bot
              uses live, and see how many signals it actually catches.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <DateField label="From (UTC)" testId="audit-from-date"
                     value={fromDate} onChange={setFromDate} maxDate={toDate} />
          <DateField label="To (UTC, exclusive)" testId="audit-to-date"
                     value={toDate} onChange={setToDate} minDate={fromDate} maxDate={new Date()} />
          <div className="flex items-end">
            <ToggleRow label="Dry-run" desc="Analyze only, no trades" checked={dryRun}
                       onChange={setDryRun} testId="audit-dry-run" />
          </div>
          <div className="flex items-end">
            <ToggleRow label="AI fallback" desc="Claude for unknowns" checked={useAi}
                       onChange={setUseAi} testId="audit-use-ai" />
          </div>
        </div>

        <div className="mt-3">
          <Label className="text-[11px] text-slate-500 mb-1 block">
            Target Telegram chat (leave empty to use each channel&apos;s configured chat_id)
          </Label>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={chatId}
              onChange={(e) => setChatId(e.target.value)}
              placeholder="e.g. 1003899685465 or -1001003899685465 or @signal_group"
              className="bg-white border-slate-200 h-10 flex-1 mono text-sm"
              data-testid="audit-chat-id"
            />
            {channelsQ.data && channelsQ.data.length > 0 && (
              <Select
                value={""}
                onValueChange={(v) => {
                  const ch = channelsQ.data.find((c) => c.id === v);
                  if (ch?.telegram_chat_id) setChatId(ch.telegram_chat_id);
                }}
              >
                <SelectTrigger className="h-10 bg-white sm:w-64" data-testid="audit-chat-picker">
                  <SelectValue placeholder="…or pick a saved channel" />
                </SelectTrigger>
                <SelectContent>
                  {channelsQ.data
                    .filter((c) => c.telegram_chat_id)
                    .map((c) => (
                      <SelectItem key={c.id} value={c.id}
                                  data-testid={`audit-chat-pick-${c.id}`}>
                        {c.name} · {c.telegram_chat_label || c.telegram_chat_id}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 mr-1">Quick range:</span>
          {presets.map((p) => (
            <button
              key={p.label}
              data-testid={`audit-preset-${p.label.toLowerCase()}`}
              onClick={() => applyPreset(...p.get())}
              className="text-xs px-3.5 py-1.5 rounded-full bg-white hover:border-[#4F46E5]/40 border border-slate-200 text-slate-600 hover:text-slate-900 transition-colors mono font-bold"
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <Button data-testid="audit-run-btn" onClick={() => run.mutate()}
                  disabled={run.isPending}
                  className="bg-[#4F46E5] hover:bg-[#4338CA]">
            {run.isPending ? (
              <>
                <ArrowsClockwise size={14} className="mr-2 animate-spin" />
                Running… {useAi ? `(AI cap ${aiCap} — up to ~${Math.max(2, Math.round(aiCap / 15))} min)` : "(fast)"}
              </>
            ) : "Run backfill"}
          </Button>
          {useAi && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5">
              <label className="text-[11px] uppercase tracking-widest text-amber-700">
                AI budget
              </label>
              <Input
                type="number"
                min={10}
                max={1000}
                step={10}
                value={aiCap}
                onChange={(e) => setAiCap(Math.max(10, Math.min(1000, Number(e.target.value) || 200)))}
                data-testid="audit-ai-cap"
                className="w-20 h-7 text-sm bg-white border-amber-200 mono"
              />
              <span className="text-[10px] text-amber-700">
                messages · higher = better recall, slower run
              </span>
            </div>
          )}
          {!dryRun && (
            <Badge variant="loss">⚠ Not dry-run: will store raw messages + place paper/live orders</Badge>
          )}
        </div>
      </div>

      {report && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <MetricCard testId="audit-metric-total" label="Total messages" value={report.total_messages} />
            <MetricCard testId="audit-metric-signals" label="Parsed signals" value={totalSignals}
                        sub="After multi-symbol split" />
            <MetricCard testId="audit-metric-recall" label="Recall"
                        value={`${recall.toFixed(1)}%`}
                        tone={recall > 90 ? "profit" : recall > 70 ? "warn" : "loss"}
                        sub={`${actions.unknown || 0} unknown`} />
            <MetricCard testId="audit-metric-symbols" label="Unique symbols"
                        value={(report.top_symbols || []).length} />
            <MetricCard testId="audit-metric-correlated" label="Correlated"
                        value={report.parser_sources?.correlated || 0}
                        tone="accent" sub="Implicit-symbol inferred" />
            <MetricCard testId="audit-metric-ocr" label="OCR recovered"
                        value={report.ocr_recovered || 0}
                        tone="accent"
                        sub={report.ocr_cache_hits
                          ? `${report.ocr_cache_hits} cached (free)`
                          : "Chart-image signals"} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="card-surface p-6">
              <p className="overline">Action-type breakdown</p>
              <h3 className="font-display text-xl mt-1 mb-4">What the parser saw</h3>
              <div className="space-y-2">
                {sortedActions.map(([action, count]) => (
                  <div key={action} data-testid={`audit-action-${action}`}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="mono text-slate-900">{action}</span>
                      <span className="mono text-slate-500">{count} · {(count * 100 / totalSignals).toFixed(1)}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${(count / maxCount) * 100}%`,
                          background: ACTION_COLORS[action] || "#3395FF",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card-surface p-6">
              <p className="overline">Top symbols detected</p>
              <h3 className="font-display text-xl mt-1 mb-4">Signal frequency</h3>
              <div className="grid grid-cols-2 gap-2">
                {(report.top_symbols || []).slice(0, 20).map(([sym, count]) => (
                  <div key={sym} className="flex items-center justify-between p-2 rounded-md bg-slate-50">
                    <span className="mono text-slate-900 text-sm">{sym}</span>
                    <span className="mono text-slate-500 text-xs">{count}</span>
                  </div>
                ))}
                {(report.top_symbols || []).length === 0 && (
                  <p className="text-xs text-slate-500 col-span-2 text-center py-4">No symbols detected</p>
                )}
              </div>
            </div>
          </div>

          <div className="card-surface p-6">
            <p className="overline">Parser gaps — messages we couldn&apos;t classify</p>
            <h3 className="font-display text-xl mt-1 mb-4">Unknown samples</h3>
            {(report.unknown_samples || []).length === 0 ? (
              <p className="text-sm text-emerald-600">✓ Zero unknowns in this range — perfect recall.</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto scrollbar-thin pr-2">
                {(report.unknown_samples || []).map((u, i) => (
                  <div key={`${u.date}-${i}`} className="p-3 rounded-md bg-amber-50 border border-amber-200">
                    <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
                      <span>{new Date(u.date).toLocaleString()}</span>
                      <Badge variant="warn">unknown</Badge>
                    </div>
                    <pre className="mono text-xs text-slate-800 whitespace-pre-wrap">{u.text}</pre>
                  </div>
                ))}
              </div>
            )}
          </div>

          {dryRun && report.sample_rows && report.sample_rows.length > 0 && (
            <div className="card-surface p-6">
              <p className="overline">Last 60 parsed rows</p>
              <h3 className="font-display text-xl mt-1 mb-4">Sample output</h3>
              <div className="overflow-x-auto scrollbar-thin">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-slate-500 text-[10px] uppercase tracking-widest border-b border-slate-200">
                      <th className="text-left py-2 px-2">Date</th>
                      <th className="text-left py-2 px-2">Action</th>
                      <th className="text-left py-2 px-2">Symbol</th>
                      <th className="text-left py-2 px-2">Side</th>
                      <th className="text-right py-2 px-2">Conf</th>
                      <th className="text-left py-2 px-2">Source</th>
                      <th className="text-left py-2 px-2">Text</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.sample_rows.slice().reverse().map((r, i) => (
                      <tr key={`${r.date}-${i}`} className="border-b border-slate-200 hover:bg-slate-50">
                        <td className="py-2 px-2 mono text-slate-500">{r.date?.slice(0, 10)}</td>
                        <td className="py-2 px-2">
                          <Badge variant={
                            r.action === "entry" ? "profit" :
                            r.action?.includes("close") ? "loss" :
                            r.action === "noise" ? "muted" :
                            r.action === "unknown" ? "warn" : "info"
                          }>{r.action}</Badge>
                        </td>
                        <td className="py-2 px-2 mono">{r.symbol || "—"}</td>
                        <td className="py-2 px-2 mono">{r.side || "—"}</td>
                        <td className="py-2 px-2 mono text-right">{(r.confidence * 100).toFixed(0)}%</td>
                        <td className="py-2 px-2 mono text-slate-500">{r.source}</td>
                        <td className="py-2 px-2 text-slate-600 max-w-md truncate" title={r.text}>{r.text}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
